import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import { Reveal, Overline } from "@/components/motion";
import { CONTACT, waLink } from "@/data/site";
import { Phone, MessageCircle, Mail, MapPin, ArrowUpRight } from "lucide-react";

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        overline="Contact"
        lines={["Let's move", "your minerals."]}
        intro="Two lines, one inbox, zero middlemen between you and the deal. Call, WhatsApp or write — a broker answers within 24 hours."
      />

      <section data-testid="contact-channels" className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="grid gap-6 lg:grid-cols-2">
          {CONTACT.phones.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div
                data-testid={`contact-phone-card-${p.id}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-navy-900/60 p-9 transition-[border-color,box-shadow] duration-500 hover:border-gold/40 hover:shadow-[0_24px_80px_-24px_rgba(212,175,55,0.25)]"
              >
                <div className="pointer-events-none absolute inset-0 spotlight opacity-0 transition-opacity duration-700 group-hover:opacity-60" />
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold/80">{p.tag}</p>
                <p className="mt-4 font-serif text-3xl tracking-tight text-slate-50 sm:text-4xl">{p.label}</p>
                <p className="mt-3 text-sm text-slate-500">Voice & WhatsApp — answered by a broker, not a bot.</p>
                <div className="mt-auto flex flex-wrap gap-3 pt-8">
                  <a
                    href={p.tel}
                    data-testid={`contact-call-${p.id}`}
                    className="inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-navy-950 transition-all duration-300 hover:bg-gold-light"
                  >
                    <Phone className="h-4 w-4" /> Call now
                  </a>
                  <a
                    href={waLink(p.wa, "Hello Tuskers Grey Investments — I'd like to discuss minerals.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-whatsapp-${p.id}`}
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-7 py-3.5 text-xs uppercase tracking-[0.22em] text-slate-200 transition-all duration-300 hover:border-gold/60 hover:text-gold"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Reveal delay={0.15} className="h-full">
            <a
              href={`mailto:${CONTACT.email}?subject=Mineral%20enquiry`}
              data-testid="contact-email-card"
              className="group flex h-full items-center justify-between gap-6 rounded-[2rem] border border-white/10 bg-navy-900/60 p-9 transition-[border-color] duration-500 hover:border-gold/40"
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold/80">Email</p>
                <p className="mt-3 font-serif text-2xl text-slate-50 sm:text-3xl">{CONTACT.email}</p>
                <p className="mt-2 text-sm text-slate-500">For mandates, term sheets and assay reports.</p>
              </div>
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-navy-950">
                <Mail className="h-5 w-5" />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.22} className="h-full">
            <div
              data-testid="contact-location-card"
              className="flex h-full items-center justify-between gap-6 rounded-[2rem] border border-white/10 bg-navy-900/60 p-9"
            >
              <div>
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold/80">Based in</p>
                <p className="mt-3 font-serif text-2xl text-slate-50 sm:text-3xl">Midlands Province</p>
                <p className="mt-2 text-sm text-slate-500">Zimbabwe — operating across the Great Dyke.</p>
              </div>
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                <MapPin className="h-5 w-5" />
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p data-testid="contact-note" className="mt-12 max-w-2xl text-sm leading-relaxed text-slate-500">
            Prefer to start on paper? Send a mineral, grade and tonnage to either line and we'll
            come back with next steps — sampling, assay and terms — within one working day.
          </p>
        </Reveal>
      </section>

      <Marquee slow items={["Call", "WhatsApp", "Write", "We answer within 24 hours"]} />

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <Reveal className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <Overline>Sister company</Overline>
            <h2 className="mt-4 max-w-xl font-serif text-2xl leading-snug text-slate-50 sm:text-3xl">
              Looking for procurement beyond mining? Visit Tuskers Grey Holdings.
            </h2>
          </div>
          <a
            href={CONTACT.holdings}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-holdings-link"
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-[0.25em] text-slate-200 transition-all duration-300 hover:border-gold/60 hover:text-gold"
          >
            tuskersgrey.com <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </section>
    </main>
  );
}
